/**
 * Auth Context - Global authentication state management
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mfaEnforced, setMfaEnforced] = useState(true);

  // Persist a full session (tokens + user/profile) returned by login/verify/enable.
  const applySession = useCallback((data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('refreshToken', data.refreshToken);
    setUser(data.user);
    setProfile(data.profile);
  }, []);

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await authAPI.getMe();
          setUser(response.data.user);
          setProfile(response.data.profile);
          if (typeof response.data.mfaEnforced === 'boolean') setMfaEnforced(response.data.mfaEnforced);
        } catch (err) {
          console.error('Auth check failed:', err);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  // Returns one of:
  //   { status: 'ok' }                      fully logged in
  //   { status: 'mfa', mfaToken }           needs a TOTP code
  //   { status: 'enroll', mfaToken, username }  must set up 2FA first
  //   { status: 'error', error }
  const login = useCallback(async (username, password) => {
    try {
      setError(null);
      const { data } = await authAPI.login(username, password);
      if (data.mfaRequired) return { status: 'mfa', mfaToken: data.mfaToken };
      if (data.enrollmentRequired) return { status: 'enroll', mfaToken: data.mfaToken, username: data.username };
      applySession(data);
      return { status: 'ok' };
    } catch (err) {
      const message = err.response?.data?.error || 'Login failed';
      setError(message);
      return { status: 'error', error: message };
    }
  }, [applySession]);

  // Complete a 2FA login by exchanging the mfaToken + code for a session.
  const verifyMfaLogin = useCallback(async (mfaToken, code) => {
    try {
      const { data } = await authAPI.verify2fa(mfaToken, code);
      applySession(data);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Invalid code' };
    }
  }, [applySession]);

  // Start enrollment → { qrDataUrl, secret, otpauthUrl }. authToken is the mfaToken
  // during a login/registration enrollment, or omitted when enabling from Settings.
  const setupMfa = useCallback(async (authToken) => {
    const { data } = await authAPI.setup2fa(authToken);
    return data;
  }, []);

  // Confirm enrollment with a code. Returns { success, backupCodes, session }.
  // The caller applies the session AFTER the user saves their backup codes
  // (applying it flips isAuthenticated and would otherwise redirect away first).
  const enableMfa = useCallback(async (code, authToken) => {
    try {
      const { data } = await authAPI.enable2fa(code, authToken);
      return { success: true, backupCodes: data.backupCodes, session: data.token ? data : null };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Invalid code' };
    }
  }, []);

  // Same status shape as login(): 'ok' | 'enroll' | 'error'. With mandatory 2FA a new
  // account returns 'enroll' and must set up 2FA before it gets a session.
  const register = useCallback(async (username, password, email, inviteToken) => {
    try {
      setError(null);
      const { data } = await authAPI.register(username, password, email, inviteToken);
      if (data.enrollmentRequired) return { status: 'enroll', mfaToken: data.mfaToken, username: data.username };
      applySession(data);
      return { status: 'ok' };
    } catch (err) {
      const message = err.response?.data?.error || 'Registration failed';
      setError(message);
      return { status: 'error', error: message };
    }
  }, [applySession]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    setUser(null);
    setProfile(null);
  }, []);

  const updateProfile = useCallback(async (data) => {
    try {
      await authAPI.updateProfile(data);
      // Refresh profile
      const response = await authAPI.getMe();
      setProfile(response.data.profile);
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Update failed' };
    }
  }, []);

  const saveAlpacaKeys = useCallback(async (apiKey, secretKey, isPaperTrading = true) => {
    try {
      await authAPI.saveAlpacaKeys(apiKey, secretKey, isPaperTrading);
      setProfile((prev) => ({ ...prev, hasAlpacaKeys: true }));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Failed to save keys' };
    }
  }, []);

  const removeAlpacaKeys = useCallback(async () => {
    try {
      await authAPI.removeAlpacaKeys();
      setProfile((prev) => ({ ...prev, hasAlpacaKeys: false }));
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Failed to remove keys' };
    }
  }, []);

  // RBAC: the current user's effective permission set, and a `can(id)` check.
  // Admin is a superuser (always true). Used to gate nav, pages, and buttons.
  const permissionSet = useMemo(() => new Set(user?.permissions || []), [user]);
  const can = useCallback(
    (permId) => !!user && (user.isAdmin || permissionSet.has(permId)),
    [user, permissionSet]
  );

  const value = {
    user,
    profile,
    loading,
    error,
    isAuthenticated: !!user,
    hasAlpacaKeys: profile?.hasAlpacaKeys || false,
    permissions: user?.permissions || [],
    can,
    mfaEnforced,
    applySession,
    login,
    verifyMfaLogin,
    setupMfa,
    enableMfa,
    register,
    logout,
    updateProfile,
    saveAlpacaKeys,
    removeAlpacaKeys,
    clearError: () => setError(null),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
